/**
 * Composer — 语音优先的输入条，Space / Session 共用。
 *
 * 默认语音(大麦克风);点键盘图标切文字,模式记进 localStorage。
 * b 方案:语音 = 录音 → 直接把音频发出去,桌面转写后直接派 —— 手机不转写、
 * 不回填输入框。文字模式照常 textarea + 发送。
 *
 * 附件:拍照 / 相册 / 文件 —— 选好直传对象存储拿 upload_id,跟 task/message
 * 一起发给桌面。选附件自动切到文字模式(要配文字 + 看附件 chip)。
 */

import { useEffect, useRef, useState } from 'react'

import { useLang } from '../contexts/LanguageContext'
import { useRecorder, type RecorderError } from '../lib/voice'
import { uploadFile, type AttachmentRef } from '../lib/api'
import { Spinner } from './ui'
import { IconMic, IconKeyboard, IconSend, IconClose, IconPlus, IconCamera, IconImage, IconFile } from './icons'

const MODE_KEY = 'cortex-m-input-mode'
type Mode = 'voice' | 'text'

function initialMode(): Mode {
  try {
    return localStorage.getItem(MODE_KEY) === 'text' ? 'text' : 'voice'
  } catch {
    return 'voice'
  }
}

const REC_ERR_KEY: Record<RecorderError, string> = {
  denied: 'voice.micDenied',
  unavailable: 'voice.micUnavailable',
  failed: 'voice.recordFailed',
}

function fmtElapsed(ms: number): string {
  const s = Math.floor(ms / 1000)
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`
}

/** 选中待发的附件 — 上传中 / 已就绪 / 失败三态。 */
interface Pending {
  localId: string
  name: string
  kind: 'image' | 'file'
  previewUrl?: string
  ref?: AttachmentRef
  status: 'uploading' | 'done' | 'error'
}

/** 已就绪附件的可序列化快照,持久化到 localStorage —— 和文字草稿(useDraft)一样
 *  扛过 in-app 浏览器调起原生相机时的整页 reload(issue #133):reload 会清空
 *  Composer 内存里的 attachments,导致「拍照→只发图片」的消息凭空消失。upload_id
 *  已经在对象存储里,序列化它即可在重挂载后恢复成可发送的 chip(预览 blob URL 无法
 *  跨 reload 保留,降级为通用文件 chip)。只存已上传完成(done)的,上传中/失败的不存。 */
interface PersistedAttachment {
  name: string
  kind: 'image' | 'file'
  ref: AttachmentRef
}

function loadPersisted(storageKey: string): Pending[] {
  try {
    const raw = localStorage.getItem(storageKey)
    if (!raw) return []
    const arr = JSON.parse(raw) as PersistedAttachment[]
    return arr
      .filter((a) => a?.ref?.upload_id)
      .map((a) => ({
        localId: crypto.randomUUID(),
        name: a.name,
        kind: a.kind,
        ref: a.ref,
        status: 'done' as const,
      }))
  } catch {
    return []
  }
}

interface Props {
  value: string
  onChange: (v: string) => void
  /** 发送文字 + 附件（已上传好的 upload_id）。 */
  onSend: (attachments: AttachmentRef[]) => void
  /** 语音:把录到的音频发出去（Space → voice-task / Session → voice-message）。
   *  onProgress 报告音频上传进度 0..1，用于上传进度条。 */
  onSendVoice: (audio: Blob, onProgress: (frac: number) => void) => Promise<void>
  sending: boolean
  placeholder: string
  /** 附件菜单展开方向。'up'(默认)用于底部固定的 composer(Session);'down' 用于
   *  顶部的 composer(Space)——否则菜单向上展开会钻进 sticky header/状态栏，
   *  把首项 "Take photo" 遮住(issue #132)。 */
  menuPlacement?: 'up' | 'down'
  /** 草稿持久化 key（和 useDraft 同一个 space/session id）—— 用来把已上传好的附件
   *  持久化到 localStorage,扛过拍照时的整页 reload(issue #133)。 */
  draftKey: string
}

export default function Composer({ value, onChange, onSend, onSendVoice, sending, placeholder, menuPlacement = 'up', draftKey }: Props) {
  const { t } = useLang()
  const attachStorageKey = `cortex-m-attach:${draftKey}`
  const [mode, setMode] = useState<Mode>(initialMode)
  const [voiceSending, setVoiceSending] = useState(false)
  const [voiceProgress, setVoiceProgress] = useState(0)
  const [errKey, setErrKey] = useState<string | null>(null)
  const [attachments, setAttachments] = useState<Pending[]>(() => loadPersisted(attachStorageKey))
  const [menuOpen, setMenuOpen] = useState(false)
  const rec = useRecorder()
  const taRef = useRef<HTMLTextAreaElement | null>(null)
  const fileRef = useRef<HTMLInputElement | null>(null)

  const switchMode = (m: Mode) => {
    setMode(m)
    setErrKey(null)
    try {
      localStorage.setItem(MODE_KEY, m)
    } catch {}
  }

  // recorder 启动报错(权限拒绝 / 不支持)→ 落文字模式 + 提示
  useEffect(() => {
    if (!rec.error) return
    setErrKey(REC_ERR_KEY[rec.error])
    if (rec.error === 'denied' || rec.error === 'unavailable') setMode('text')
  }, [rec.error])

  // textarea 自动撑高
  useEffect(() => {
    const el = taRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${Math.min(el.scrollHeight, 144)}px`
  }, [value])

  // 卸载时回收图片预览 URL —— 用 ref 读最新值，effect 依赖空数组只在卸载跑，
  // 否则 attachments 一变就会用旧闭包 revoke 掉仍在显示的 URL。
  const attachmentsRef = useRef<Pending[]>([])
  attachmentsRef.current = attachments
  useEffect(() => () => {
    attachmentsRef.current.forEach((a) => a.previewUrl && URL.revokeObjectURL(a.previewUrl))
  }, [])

  // 已就绪附件持久化到 localStorage,扛过拍照触发的整页 reload(issue #133)。
  // 只存上传完成(done + 有 ref)的;没有就清掉 key(发送成功 / 移除后回到干净态)。
  useEffect(() => {
    try {
      const ready = attachments
        .filter((a) => a.status === 'done' && a.ref)
        .map((a) => ({ name: a.name, kind: a.kind, ref: a.ref! }))
      if (ready.length) localStorage.setItem(attachStorageKey, JSON.stringify(ready))
      else localStorage.removeItem(attachStorageKey)
    } catch {
      /* private mode / quota — 退回原内存行为 */
    }
  }, [attachStorageKey, attachments])

  const stopAndSend = async () => {
    const blob = await rec.stop()
    if (!blob) return
    setVoiceSending(true)
    setVoiceProgress(0)
    setErrKey(null)
    try {
      await onSendVoice(blob, setVoiceProgress)
    } catch {
      // 发送错误由父组件在页面 error 区展示，这里只复位
    } finally {
      setVoiceSending(false)
      setVoiceProgress(0)
    }
  }

  // ── 附件选择 ──
  const openPicker = (accept: string, capture: boolean, multiple: boolean) => {
    setMenuOpen(false)
    const el = fileRef.current
    if (!el) return
    el.accept = accept
    el.multiple = multiple
    if (capture) el.setAttribute('capture', 'environment')
    else el.removeAttribute('capture')
    el.click()
  }

  const onFilesPicked = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])
    e.target.value = ''
    if (!files.length) return
    switchMode('text')
    for (const file of files) {
      const localId = crypto.randomUUID()
      const kind: 'image' | 'file' = file.type.startsWith('image/') ? 'image' : 'file'
      const previewUrl = kind === 'image' ? URL.createObjectURL(file) : undefined
      setAttachments((prev) => [...prev, { localId, name: file.name || 'file', kind, previewUrl, status: 'uploading' }])
      uploadFile(file)
        .then((ref) => setAttachments((prev) =>
          prev.map((a) => (a.localId === localId ? { ...a, ref, status: 'done' as const } : a))))
        .catch(() => setAttachments((prev) =>
          prev.map((a) => (a.localId === localId ? { ...a, status: 'error' as const } : a))))
    }
  }

  const removeAttachment = (localId: string) => {
    setAttachments((prev) => {
      const hit = prev.find((a) => a.localId === localId)
      if (hit?.previewUrl) URL.revokeObjectURL(hit.previewUrl)
      return prev.filter((a) => a.localId !== localId)
    })
  }

  const uploading = attachments.some((a) => a.status === 'uploading')
  const readyRefs = attachments.filter((a) => a.status === 'done' && a.ref).map((a) => a.ref!)
  const canSend = (value.trim().length > 0 || readyRefs.length > 0) && !uploading && !sending

  // 已选附件的 chip 列表 —— voice 空闲态和文字态共用,拍照后两种模式都能看到/移除/发送。
  const attachmentChips = attachments.length > 0 && (
    <div className="mb-2 flex flex-wrap gap-2">
      {attachments.map((a) => (
        <div
          key={a.localId}
          className="relative flex items-center gap-2 rounded-lg border border-line-subtle bg-panel py-1.5 pl-1.5 pr-7"
        >
          {a.kind === 'image' && a.previewUrl ? (
            <img src={a.previewUrl} alt="" className="h-9 w-9 rounded object-cover" />
          ) : (
            <span className="flex h-9 w-9 items-center justify-center rounded bg-elevated text-muted">
              <IconFile className="h-4 w-4" />
            </span>
          )}
          <span className="max-w-[7.5rem] truncate text-xs text-secondary">{a.name}</span>
          {a.status === 'uploading' && <Spinner className="h-3.5 w-3.5 text-accent" />}
          {a.status === 'error' && <span className="text-xs text-error">{t('attach.failed')}</span>}
          <button
            onClick={() => removeAttachment(a.localId)}
            aria-label={t('attach.remove')}
            className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-elevated text-faint active:bg-hover"
          >
            <IconClose className="h-3 w-3" />
          </button>
        </div>
      ))}
    </div>
  )

  const doSend = () => {
    if (!canSend) return
    onSend(readyRefs)
    attachments.forEach((a) => a.previewUrl && URL.revokeObjectURL(a.previewUrl))
    setAttachments([])
  }

  const errLine = errKey && <p className="mb-1.5 px-1 text-xs text-error">{t(errKey)}</p>

  // ── 录音中 ──
  if (rec.recording) {
    return (
      <div>
        {errLine}
        <div className="flex items-center gap-2">
          <button
            onClick={() => void stopAndSend()}
            className="flex min-h-tap flex-1 items-center gap-3 rounded-lg border border-accent/40 bg-panel px-4"
          >
            <span
              className="h-3 w-3 shrink-0 rounded-full bg-accent transition-transform"
              style={{ transform: `scale(${1 + rec.level * 0.8})` }}
            />
            <span className="flex-1 text-left text-sm text-primary">{t('voice.tapToStop')}</span>
            <span className="font-mono text-xs tabular-nums text-accent-light">{fmtElapsed(rec.elapsedMs)}</span>
          </button>
          <button
            onClick={rec.cancel}
            aria-label={t('voice.cancel')}
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-line text-muted active:bg-hover"
          >
            <IconClose className="h-5 w-5" />
          </button>
        </div>
        <p className="mt-1.5 px-1 text-xs text-faint">{t('voice.recordingHint')}</p>
      </div>
    )
  }

  // ── 发送中(音频上传 → 桌面转写) ──
  if (voiceSending) {
    const pct = Math.round(voiceProgress * 100)
    const uploading = voiceProgress < 1
    return (
      <div className="relative min-h-tap overflow-hidden rounded-lg border border-line-subtle bg-panel">
        <div className="flex items-center gap-2.5 px-4 py-3">
          <Spinner className="h-4 w-4 text-accent" />
          <span className="flex-1 text-sm text-muted">
            {uploading ? t('voice.uploading') : t('voice.sending')}
          </span>
          {uploading && voiceProgress > 0 && (
            <span className="font-mono text-xs tabular-nums text-accent-light">{pct}%</span>
          )}
        </div>
        {uploading && (
          <div
            className="absolute bottom-0 left-0 h-0.5 bg-accent transition-[width] duration-150"
            style={{ width: `${pct}%` }}
          />
        )}
      </div>
    )
  }

  const hiddenInput = (
    <input ref={fileRef} type="file" className="hidden" onChange={onFilesPicked} />
  )

  // 附件选择菜单 —— voice 模式 attach 按钮在屏幕右侧（Tap to speak 占满左侧），
  // 菜单需向左展开；text 模式 attach 按钮在最左，菜单向右展开。固定 left-0 在
  // voice 模式下会撞屏幕右边界,选项文字被截断(GitHub issue #129)。
  const attachMenu = menuOpen && (
    <>
      <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(false)} />
      <div className={`absolute z-20 w-44 overflow-hidden rounded-lg border border-line-subtle bg-elevated shadow-lg ${menuPlacement === 'down' ? 'top-full mt-2' : 'bottom-full mb-2'} ${mode === 'voice' ? 'right-0' : 'left-0'}`}>
        {[
          { icon: IconCamera, label: t('attach.camera'), fn: () => openPicker('image/*', true, false) },
          { icon: IconImage, label: t('attach.photo'), fn: () => openPicker('image/*', false, true) },
          { icon: IconFile, label: t('attach.file'), fn: () => openPicker('', false, true) },
        ].map(({ icon: Icon, label, fn }) => (
          <button
            key={label}
            onClick={fn}
            className="flex w-full items-center gap-3 px-3.5 py-3 text-left text-sm text-primary active:bg-hover"
          >
            <Icon className="h-4 w-4 shrink-0 text-muted" />
            {label}
          </button>
        ))}
      </div>
    </>
  )

  const attachButton = (
    <div className="relative">
      <button
        onClick={() => setMenuOpen((v) => !v)}
        aria-label={t('attach.add')}
        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-line-subtle bg-panel text-muted active:bg-hover"
      >
        <IconPlus className="h-5 w-5" />
      </button>
      {attachMenu}
    </div>
  )

  // ── 语音空闲态(默认) ──
  // 仅在没文字时进语音空闲态;**有附件也允许**(issue #133:拍照后不能再被锁死在
  // 文字态、要能切回语音)。已选附件以 chip 显示在上方,并给一个发送按钮 —— 仅图片
  // 也能从语音态直接发出去。注意:语音消息走 voice 端点只带音频,录音不携带这些图片
  // (后端不支持图+音同发);chip + 发送按钮让用户能先把图片发掉,不会凭空丢失。
  if (mode === 'voice' && !value) {
    return (
      <div className="relative">
        {errLine}
        {hiddenInput}
        {attachmentChips}
        <div className="flex items-center gap-2">
          <button
            onClick={() => void rec.start()}
            className="flex min-h-tap flex-1 items-center justify-center gap-2 rounded-lg bg-accent text-base font-medium text-white transition-colors active:bg-accent-hover"
          >
            <IconMic className="h-5 w-5" />
            {t('voice.tapToSpeak')}
          </button>
          {attachButton}
          {readyRefs.length > 0 && (
            <button
              onClick={doSend}
              disabled={!canSend}
              aria-label={t('common.send')}
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-accent text-white transition-colors active:bg-accent-hover disabled:bg-elevated disabled:text-faint"
            >
              {sending ? <Spinner className="h-4 w-4" /> : <IconSend className="h-5 w-5" />}
            </button>
          )}
          <button
            onClick={() => switchMode('text')}
            aria-label={t('voice.switchToKeyboard')}
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-line-subtle bg-panel text-muted active:bg-hover"
          >
            <IconKeyboard className="h-5 w-5" />
          </button>
        </div>
      </div>
    )
  }

  // ── 文字态 ──
  // 没文字 / 没就绪附件 / 没在上传 → 纯空,trailing 是「切语音」麦克风。
  const emptyCompose = !value.trim() && readyRefs.length === 0 && !uploading
  // 有就绪附件但还没打字 → 既要能「仅图片发送」,又要能「切去语音叠加」(issue #133):
  // 同时给麦克风(切语音)+ 发送两个按钮,不再被发送按钮独占 trailing 槽把语音锁死。
  const attachmentsOnly = readyRefs.length > 0 && !value.trim() && !uploading
  const micToggle = (
    <button
      onClick={() => switchMode('voice')}
      aria-label={t('voice.switchToVoice')}
      className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-line-subtle bg-panel text-muted active:bg-hover"
    >
      <IconMic className="h-5 w-5" />
    </button>
  )
  const sendButton = (
    <button
      onClick={doSend}
      disabled={!canSend}
      aria-label={t('common.send')}
      className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-accent text-white transition-colors active:bg-accent-hover disabled:bg-elevated disabled:text-faint"
    >
      {sending ? <Spinner className="h-4 w-4" /> : <IconSend className="h-5 w-5" />}
    </button>
  )
  return (
    <div className="relative">
      {errLine}
      {hiddenInput}

      {attachmentChips}

      <div className="flex items-end gap-2">
        {attachButton}
        <textarea
          ref={taRef}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          rows={1}
          className="max-h-36 min-h-tap flex-1 resize-none rounded-lg border border-line-subtle bg-panel px-3.5 py-2.5 text-base text-primary placeholder:text-faint focus:border-line focus:outline-none"
        />
        {emptyCompose ? (
          micToggle
        ) : attachmentsOnly ? (
          <>
            {micToggle}
            {sendButton}
          </>
        ) : (
          sendButton
        )}
      </div>
    </div>
  )
}
